import * as Select from '@radix-ui/react-select';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DropdownItems } from '~/chords/DropdownSingleSelect/components/DropdownItems';
import { DropdownNoOptionsAvailable } from '~/chords/DropdownSingleSelect/components/DropdownNoOptionsAvailable';
import { DropdownSearch } from '~/chords/DropdownSingleSelect/components/DropdownSearch';
import type {
  DropdownContentOptions,
  DropdownSearchOptions,
  InternalDropdownOption,
  InternalDropdownOptions,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Scrollbars } from '~/components/Scrollbars/Scrollbars';
import { cn } from '~/lib/cn';

interface DropdownMenuProps extends RadixUiProps {
  options: InternalDropdownOptions;
  contentOptions: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedOption?: InternalDropdownOption;
  onSelectOption: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownMenu({
  options,
  contentOptions,
  searchOptions,
  searchQuery,
  onSearchQueryChange,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  OptionTemplate,
  radix = {},
}: DropdownMenuProps) {
  const { t } = useTranslation('common');
  const isOneOrMoreOptionsVisible = options.some((group) =>
    group.some((option) => option.isVisible),
  );
  const hasAnyOptions = options.some((group) => group.length);
  const searchQueryTrimmed = searchQuery.trim();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);
  const [refocusTrigger, setRefocusTrigger] = useState(0);

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  // Track when options change and increment trigger
  useEffect(() => {
    if (searchOptions?.focusOnOpen) {
      setRefocusTrigger((prev) => prev + 1);
    }
  }, [options, searchOptions?.focusOnOpen]);

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    // If the search input field is empty or not enabled, allow the dropdown to close as normal.
    if (!searchOptions?.isEnabled || !searchQueryTrimmed) {
      return;
    }

    // If the search input field is populated and the user presses the Escape key,
    // clear the search input and focus the cursor into it.
    event.preventDefault();

    onSearchQueryChange('');

    timeoutRef.current = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  };

  radix.selectGroup = {
    ...radix.selectGroup,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Navigate to the search input when the user presses ArrowUp on the first option.
      if (
        event.key === 'ArrowUp' &&
        event.target === event.currentTarget.firstChild
      ) {
        searchInputRef.current?.focus();
      }
    },
  };

  return (
    <Select.Portal>
      <Select.Content
        // Prevents the dropdown menu from overlapping the dropdown trigger.
        // https://www.radix-ui.com/primitives/docs/components/select#content
        position="popper"
        {...radix.selectContent}
        className={cn(
          'mt-xs min-w-[var(--radix-popper-anchor-width)] overflow-auto text-body-sm text-default',
          'rounded-xs border shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
          radix.selectContent?.className,
        )}
        style={
          {
            // This is used to calculate the scroll height of the dropdown menu.
            '--dropdown-menu-search-height': searchOptions?.isEnabled
              ? '72px'
              : '0px',
            zIndex: contentOptions.zIndex,
          } as React.CSSProperties
        }
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {!searchQuery && !hasAnyOptions ? (
          <DropdownNoOptionsAvailable radix={radix} />
        ) : (
          <>
            {searchOptions?.isEnabled ? (
              <DropdownSearch
                value={searchQuery}
                onChange={onSearchQueryChange}
                inputRef={searchInputRef}
                focusOnOpen={searchOptions.focusOnOpen}
                refocusTrigger={refocusTrigger}
              />
            ) : null}
            <Select.Viewport {...radix.selectViewport}>
              <Scrollbars
                radix={{
                  ...radix,
                  scrollAreaViewport: {
                    className: cn(
                      'max-h-dropdown-single-select-scroll',
                      radix.scrollAreaViewport?.className,
                    ),
                    ...radix.scrollAreaViewport,
                  },
                }}
              >
                {isOneOrMoreOptionsVisible ? (
                  <DropdownItems
                    options={options}
                    searchQuery={searchQueryTrimmed}
                    selectedOption={selectedOption}
                    onSelectOption={onSelectOption}
                    showCheckIcon={showCheckIcon}
                    OptionTemplate={OptionTemplate}
                    radix={radix}
                  />
                ) : (
                  <DropdownNoOptionsAvailable
                    labelText={t('dropdownTree.noMatchingOptionsText', {
                      searchQuery: searchQueryTrimmed,
                    })}
                    labelNode={
                      <span className="text-emphasis-stronger">
                        <Trans
                          i18nKey="dropdownTree.noMatchingOptionsHtml"
                          t={t}
                          values={{
                            searchQuery: searchQueryTrimmed,
                          }}
                          components={{
                            strong: (
                              <span className="font-semibold text-default" />
                            ),
                          }}
                        />
                      </span>
                    }
                    radix={radix}
                  />
                )}
              </Scrollbars>
            </Select.Viewport>
          </>
        )}
      </Select.Content>
    </Select.Portal>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
